import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import VariantPricingModal from '../components/VariantPricingModal';
import EditProductModal from '../components/EditProductModal';
import { ArrowLeft, Package, RefreshCw, DollarSign, Edit, RotateCw, TrendingUp, Plus, Minus, Calculator, Info, Tag, Clock, ExternalLink } from 'lucide-react';

const ProductDetail = ({ user, onLogout }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [brand, setBrand] = useState(null);
  const [variants, setVariants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshingPrice, setRefreshingPrice] = useState(false);
  const [refreshingMBP, setRefreshingMBP] = useState(false);
  const [selectedImage, setSelectedImage] = useState(0);
  const [showAllSpecs, setShowAllSpecs] = useState(false);
  const [showPricingModal, setShowPricingModal] = useState(false);
  const [hasMissingPrices, setHasMissingPrices] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [showMBPModal, setShowMBPModal] = useState(false);
  const [platformPrices, setPlatformPrices] = useState(null);
  const [loadingPlatforms, setLoadingPlatforms] = useState(false);
  const [manualMBP, setManualMBP] = useState({ platform: '', mbp: '', url: '' });
  // Separate state for each platform's manual entry
  const [cashifyEntry, setCashifyEntry] = useState({ mbp: '', url: '' });
  const [budliEntry, setBudliEntry] = useState({ mbp: '', url: '' });
  const [recycledeviceEntry, setRecycledeviceEntry] = useState({ mbp: '', url: '' });
  const [calculatingOurMBP, setCalculatingOurMBP] = useState(false);
  
  // State for bulk editing all variants' MBP
  const [variantMBPEdits, setVariantMBPEdits] = useState({});
  const [savingBulkMBP, setSavingBulkMBP] = useState(false);
  
  // New states for tabs and variant BB pricing
  const [activeMainTab, setActiveMainTab] = useState('info'); // 'info', 'selling', 'buyback'
  const [variantStorages, setVariantStorages] = useState([]);
  const [selectedStorage, setSelectedStorage] = useState(null);
  const [variantBBPrices, setVariantBBPrices] = useState({});
  const [marginPercent, setMarginPercent] = useState(5);
  const [refreshingVariant, setRefreshingVariant] = useState(false);
  const [savingVariantMBP, setSavingVariantMBP] = useState(false);
  
  // Add ZUPC Modal states
  const [showAddZupcModal, setShowAddZupcModal] = useState(false);
  const [newZupc, setNewZupc] = useState({ storage: '', color: '', flipkart_price: '', amazon_price: '' });
  const [addingZupc, setAddingZupc] = useState(false);

  useEffect(() => {
    fetchProductDetails();
  }, [id]);

  const handleRefreshPrice = async () => {
    try {
      setRefreshing(true);
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/products/${id}/refresh-flipkart-price`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      const data = await response.json();

      if (response.ok && data.mrp && data.flipkart_price) {
        alert(`✓ Price refreshed!\nMRP: ₹${data.mrp.toLocaleString()}\nFSP: ₹${data.flipkart_price.toLocaleString()}`);
        fetchProductDetails(); // Reload data
      } else {
        alert('⚠️ ' + (data.message || 'Unable to fetch price from Flipkart'));
      }
    } catch (error) {
      console.error('Error refreshing price:', error);
      alert('Failed to refresh price');
    } finally {
      setRefreshing(false);
    }
  };

  const handleRefreshMBP = async () => {
    try {
      setRefreshingMBP(true);
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/cashify-mbp/refresh/single/${id}`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      const data = await response.json();

      if (response.ok && data.success) {
        const result = data.result;
        if (result.status === 'SUCCESS') {
          alert(`✓ MBP refreshed!\nCashify MBP: ₹${result.mbp.toLocaleString()}`);
        } else if (result.status === 'MBP_NOT_FOUND') {
          alert('⚠️ Product not found on Cashify');
        } else {
          alert('⚠️ ' + (result.error || 'Unable to fetch MBP from Cashify'));
        }
        fetchProductDetails(); // Reload data
      } else {
        alert('⚠️ ' + (data.detail || 'Failed to refresh MBP'));
      }
    } catch (error) {
      console.error('Error refreshing MBP:', error);
      alert('Failed to refresh MBP');
    } finally {
      setRefreshingMBP(false);
    }
  };

  const fetchPlatformPrices = async () => {
    try {
      setLoadingPlatforms(true);
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/mbp-platforms/prices/${id}`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        setPlatformPrices(data);
      }
    } catch (error) {
      console.error('Error fetching platform prices:', error);
    } finally {
      setLoadingPlatforms(false);
    }
  };

  const handleManualMBPSubmit = async () => {
    if (!manualMBP.platform || !manualMBP.mbp) {
      alert('Please select a platform and enter MBP');
      return;
    }
    
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/mbp-platforms/manual/${id}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify({
            platform: manualMBP.platform,
            mbp: parseFloat(manualMBP.mbp),
            url: manualMBP.url || null
          })
        }
      );
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        alert(`✓ Manual MBP saved for ${manualMBP.platform}!\nOur MBP: ₹${data.our_mbp?.toLocaleString() || 'Calculating...'}`);
        setManualMBP({ platform: '', mbp: '', url: '' });
        fetchProductDetails();
        fetchPlatformPrices();
      } else {
        alert('⚠️ ' + (data.detail || 'Failed to save manual MBP'));
      }
    } catch (error) {
      console.error('Error saving manual MBP:', error);
      alert('Failed to save manual MBP');
    }
  };

  const handleCalculateOurMBP = async (marginPercent = 5) => {
    try {
      setCalculatingOurMBP(true);
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/mbp-platforms/calculate-our-mbp/${id}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify({
            margin_percent: marginPercent,
            calculation_method: 'average'
          })
        }
      );
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        alert(`✓ Our MBP calculated!\nOur MBP: ₹${data.our_mbp.toLocaleString()}\nBased on ${data.calculation.prices_used} platform prices\nAverage: ₹${data.calculation.average.toLocaleString()}\nMargin: ${data.calculation.margin_percent}%`);
        fetchProductDetails();
        fetchPlatformPrices();
      } else {
        alert('⚠️ ' + (data.message || data.detail || 'Failed to calculate our MBP'));
      }
    } catch (error) {
      console.error('Error calculating our MBP:', error);
      alert('Failed to calculate our MBP');
    } finally {
      setCalculatingOurMBP(false);
    }
  };

  const [refreshingAllPlatforms, setRefreshingAllPlatforms] = useState(false);

  const handleRefreshAllPlatforms = async () => {
    try {
      setRefreshingAllPlatforms(true);
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/mbp-platforms/refresh-all/${id}`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        const results = data.results;
        let message = '✓ Platform refresh complete!\n\n';
        
        if (results.cashify?.status === 'SUCCESS') {
          message += `Cashify: ₹${results.cashify.mbp?.toLocaleString()}\n`;
        } else {
          message += `Cashify: ${results.cashify?.status || 'Error'}\n`;
        }
        
        if (results.budli?.status === 'SUCCESS') {
          message += `Budli: ₹${results.budli.mbp?.toLocaleString()}\n`;
        } else {
          message += `Budli: ${results.budli?.message || results.budli?.status || 'Error'}\n`;
        }
        
        if (results.recycledevice?.status === 'SUCCESS') {
          message += `RecycleDevice: ₹${results.recycledevice.mbp?.toLocaleString()}\n`;
        } else {
          message += `RecycleDevice: ${results.recycledevice?.message || results.recycledevice?.status || 'Error'}\n`;
        }
        
        if (data.our_mbp) {
          message += `\nOur MBP: ₹${data.our_mbp.toLocaleString()}`;
        }
        
        alert(message);
        fetchProductDetails();
        fetchPlatformPrices();
      } else {
        alert('⚠️ ' + (data.detail || 'Failed to refresh platforms'));
      }
    } catch (error) {
      console.error('Error refreshing all platforms:', error);
      alert('Failed to refresh platforms');
    } finally {
      setRefreshingAllPlatforms(false);
    }
  };

  const openMBPModal = () => {
    setShowMBPModal(true);
    fetchPlatformPrices();
  };

  const fetchProductDetails = async () => {
    try {
      setLoading(true);
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/products/${id}`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );
      const data = await response.json();
      
      setProduct(data.product);
      setBrand(data.brand);
      setVariants(data.variants || []);

      // Check if any variants are missing prices
      const missingPrices = (data.variants || []).some(v => !v.mrp || !v.fsp);
      setHasMissingPrices(missingPrices);
      
      // Fetch variant storages for BB pricing
      fetchVariantStorages();
    } catch (error) {
      console.error('Error fetching product details:', error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch variant storages for BB pricing
  const fetchVariantStorages = async (currentSelectedStorage = null) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/mbp-platforms/variant/${id}/storages`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        setVariantStorages(data.storages || []);
        
        // Use passed storage or current selectedStorage
        const storageToSelect = currentSelectedStorage || selectedStorage;
        
        // Select first storage by default OR update current selection's data
        if (data.storages?.length > 0) {
          if (!storageToSelect) {
            // First load - select first storage
            const firstStorage = data.storages[0];
            setSelectedStorage(firstStorage.storage_display || firstStorage.storage);
            setVariantBBPrices(firstStorage);
          } else {
            // Refresh - update current selection's data
            const currentStorageData = data.storages.find(s => 
              (s.storage_display || s.storage) === storageToSelect
            );
            if (currentStorageData) {
              setVariantBBPrices(currentStorageData);
            }
          }
        }
      }
    } catch (error) {
      console.error('Error fetching variant storages:', error);
    }
  };

  // Handle storage selection change
  const handleStorageChange = (storageDisplay) => {
    setSelectedStorage(storageDisplay);
    const storageData = variantStorages.find(s => (s.storage_display || s.storage) === storageDisplay);
    if (storageData) {
      setVariantBBPrices(storageData);
    }
  };

  // Refresh BB prices for selected variant
  const handleRefreshVariantBB = async () => {
    if (!selectedStorage) return;
    
    try {
      setRefreshingVariant(true);
      
      // Find the actual storage value (not the display value)
      const storageData = variantStorages.find(s => (s.storage_display || s.storage) === selectedStorage);
      const actualStorage = storageData?.storage || selectedStorage;
      
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/mbp-platforms/variant/${id}/refresh/${encodeURIComponent(actualStorage)}`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );
      
      const data = await response.json();
      
      if (response.ok) {
        alert(`✓ BB Prices refreshed for ${selectedStorage}!\n\nCashify: ${data.results?.cashify?.status === 'SUCCESS' ? '₹' + data.results.cashify.mbp?.toLocaleString() : data.results?.cashify?.status}\nBudli: ${data.results?.budli?.status === 'SUCCESS' ? '₹' + data.results.budli.mbp?.toLocaleString() : data.results?.budli?.status}\nRecycleDevice: ${data.results?.recycledevice?.status === 'SUCCESS' ? '₹' + data.results.recycledevice.mbp?.toLocaleString() : data.results?.recycledevice?.status}`);
        fetchVariantStorages();
      } else {
        alert('Failed to refresh: ' + (data.detail || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error refreshing variant BB:', error);
      alert('Failed to refresh BB prices');
    } finally {
      setRefreshingVariant(false);
    }
  };

  // Scrape single platform MBP
  const [scrapingPlatform, setScrapingPlatform] = useState(null);
  
  const handleScrapeSinglePlatform = async (platform) => {
    if (!selectedStorage) return;
    
    try {
      setScrapingPlatform(platform);
      
      const storageData = variantStorages.find(s => (s.storage_display || s.storage) === selectedStorage);
      const actualStorage = storageData?.storage || selectedStorage;
      
      // Get the URL from the input field first, then fall back to saved URL
      let platformUrl = '';
      if (platform === 'cashify') {
        platformUrl = cashifyEntry.url || variantBBPrices?.cashify_mbp_url || '';
      } else if (platform === 'budli') {
        platformUrl = budliEntry.url || variantBBPrices?.budli_mbp_url || '';
      } else if (platform === 'recycledevice') {
        platformUrl = recycledeviceEntry.url || variantBBPrices?.recycledevice_mbp_url || '';
      }
      
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/mbp-platforms/variant/${id}/refresh-single/${encodeURIComponent(actualStorage)}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify({
            platform: platform,
            url: platformUrl
          })
        }
      );
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        const platformName = platform === 'cashify' ? 'Cashify' : platform === 'budli' ? 'Budli' : 'RecycleDevice';
        alert(`✓ ${platformName} price updated!\n\nMBP: ₹${data.mbp?.toLocaleString() || 'Not found'}`);
        fetchVariantStorages();
      } else {
        alert('Failed to scrape: ' + (data.detail || data.message || 'Price not found - try entering URL manually'));
      }
    } catch (error) {
      console.error('Error scraping platform:', error);
      alert('Failed to scrape platform');
    } finally {
      setScrapingPlatform(null);
    }
  };

  // Format last scraped time
  const formatLastScraped = (timestamp) => {
    if (!timestamp) return null;
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffHours / 24);
    
    if (diffDays > 0) return `${diffDays}d ago`;
    if (diffHours > 0) return `${diffHours}h ago`;
    return 'Just now';
  };

  // Calculate Our MBP for variant
  const handleCalculateVariantOurMBP = async () => {
    if (!selectedStorage) return;
    
    try {
      setSavingVariantMBP(true);
      
      // Find the actual storage value (not the display value)
      const storageData = variantStorages.find(s => (s.storage_display || s.storage) === selectedStorage);
      const actualStorage = storageData?.storage || selectedStorage;
      
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/mbp-platforms/variant/${id}/calculate-our-mbp`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify({
            storage: actualStorage,
            margin_percent: marginPercent
          })
        }
      );
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        alert(`✓ Our MBP saved for ${selectedStorage}!\n\nOur MBP: ₹${data.our_mbp?.toLocaleString()}\nMargin: ${marginPercent}%`);
        fetchVariantStorages();
      } else {
        alert('Failed: ' + (data.message || data.detail || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error calculating variant MBP:', error);
      alert('Failed to calculate Our MBP');
    } finally {
      setSavingVariantMBP(false);
    }
  };

  // Save manual MBP for a specific platform on the selected variant
  const handleSaveVariantManualMBP = async (platform, mbp, url = '') => {
    if (!selectedStorage || !platform || !mbp) return;
    
    // Capture current storage before async operation
    const currentStorage = selectedStorage;
    
    try {
      // Find the actual storage value (not the display value)
      const storageData = variantStorages.find(s => (s.storage_display || s.storage) === selectedStorage);
      const actualStorage = storageData?.storage || selectedStorage;
      
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/mbp-platforms/variant/${id}/manual`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify({
            storage: actualStorage,
            platform: platform,
            mbp: mbp,
            url: url || null  // Include URL if provided
          })
        }
      );
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        alert(`✓ ${platform.toUpperCase()} price ${url ? 'and URL ' : ''}saved: ₹${mbp.toLocaleString()}`);
        setManualMBP({ platform: '', mbp: '', url: '' });
        // Pass current storage to ensure correct data is refreshed
        await fetchVariantStorages(currentStorage);
      } else {
        alert('Failed: ' + (data.detail || data.message || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error saving manual MBP:', error);
      alert('Failed to save manual MBP');
    }
  };

  // Save MBP for a specific variant (for table view)
  const handleSaveVariantMBP = async (storageKey, platform, mbp, url) => {
    if (!storageKey || !platform || !mbp) return;
    
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/mbp-platforms/variant/${id}/manual`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify({
            storage: storageKey,
            platform: platform,
            mbp: parseFloat(mbp),
            url: url || null
          })
        }
      );
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        // Clear the edit field for this variant/platform
        setVariantMBPEdits(prev => {
          const newEdits = { ...prev };
          if (newEdits[storageKey]) {
            delete newEdits[storageKey][`${platform}_mbp`];
            delete newEdits[storageKey][`${platform}_url`];
          }
          return newEdits;
        });
        // Refresh data
        await fetchVariantStorages();
        return true;
      } else {
        alert('Failed: ' + (data.detail || data.message || 'Unknown error'));
        return false;
      }
    } catch (error) {
      console.error('Error saving MBP:', error);
      alert('Failed to save MBP');
      return false;
    }
  };

  // Get edit value for a variant/platform
  const getEditValue = (storageKey, field) => {
    return variantMBPEdits[storageKey]?.[field] ?? '';
  };

  // Set edit value for a variant/platform
  const setEditValue = (storageKey, field, value) => {
    setVariantMBPEdits(prev => ({
      ...prev,
      [storageKey]: {
        ...(prev[storageKey] || {}),
        [field]: value
      }
    }));
  };

  // Get competition average for current variant
  const getVariantCompetitionAvg = () => {
    const prices = [
      variantBBPrices?.cashify_mbp,
      variantBBPrices?.budli_mbp,
      variantBBPrices?.recycledevice_mbp
    ].filter(p => p && p > 0);
    
    if (prices.length === 0) return 0;
    return Math.round(prices.reduce((a, b) => a + b, 0) / prices.length);
  };

  // Calculate Our MBP locally
  const getCalculatedOurMBP = () => {
    const avg = getVariantCompetitionAvg();
    if (avg === 0) return 0;
    return Math.round(avg * (1 - marginPercent / 100));
  };

  // Add new ZUPC variant
  const handleAddZupc = async () => {
    if (!newZupc.storage || !newZupc.color) {
      alert('Please enter both Storage and Color');
      return;
    }
    
    try {
      setAddingZupc(true);
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/scrape/products/${id}/add-zupc`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify({
            storage: newZupc.storage.trim(),
            color: newZupc.color.trim(),
            flipkart_price: newZupc.flipkart_price ? parseFloat(newZupc.flipkart_price) : null,
            amazon_price: newZupc.amazon_price ? parseFloat(newZupc.amazon_price) : null
          })
        }
      );
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        alert(`ZUPC Created: ${data.zupc.zupc_code}`);
        setShowAddZupcModal(false);
        setNewZupc({ storage: '', color: '', flipkart_price: '', amazon_price: '' });
        fetchProductDetails(); // Refresh the page data
      } else {
        alert(data.detail || 'Failed to create ZUPC');
      }
    } catch (error) {
      console.error('Error adding ZUPC:', error);
      alert('Failed to add ZUPC variant');
    } finally {
      setAddingZupc(false);
    }
  };

  const handleRefreshFSP = async () => {
    try {
      setRefreshingPrice(true);
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/products/${id}/refresh-flipkart-price`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      const data = await response.json();

      if (response.ok) {
        alert(`FSP updated!\nOld: ₹${product.flipkart_price?.toLocaleString()}\nNew: ₹${data.flipkart_price?.toLocaleString()}`);
        fetchProductDetails();
      } else {
        alert(data.detail || 'Failed to refresh price');
      }
    } catch (error) {
      console.error('Error refreshing price:', error);
      alert('Failed to refresh price');
    } finally {
      setRefreshingPrice(false);
    }
  };

  if (loading) {
    return (
      <Layout user={user} onLogout={onLogout}>
        <div className="flex items-center justify-center h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </Layout>
    );
  }

  if (!product) {
    return (
      <Layout user={user} onLogout={onLogout}>
        <div className="p-6">
          <div className="text-center py-12">
            <Package className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900">Product not found</h3>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="p-6">
        {/* Modals */}
        <VariantPricingModal
          isOpen={showPricingModal}
          onClose={() => setShowPricingModal(false)}
          product={product}
          variants={variants}
          onSuccess={fetchProductDetails}
        />

        <EditProductModal
          isOpen={showEditModal}
          onClose={() => setShowEditModal(false)}
          product={product}
          onSuccess={fetchProductDetails}
        />

        {/* Header */}
        <div className="mb-6 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/products')}
              className="p-2 hover:bg-muted rounded-lg"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div className="flex items-center gap-4">
              {/* Brand Logo */}
              {brand?.logo_url ? (
                <img 
                  src={brand.logo_url} 
                  alt={brand?.name}
                  className="w-12 h-12 object-contain rounded-lg bg-white p-1 shadow-sm border border-gray-100"
                />
              ) : (
                <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center shadow-sm">
                  <Package className="w-6 h-6 text-gray-400" />
                </div>
              )}
              <div>
                <div className="flex items-center gap-3">
                  <h1 className="text-3xl font-bold text-foreground">{product.model_name}</h1>
                  <span className={`text-xs px-3 py-1 rounded ${
                    product.is_active
                      ? 'bg-success/10 text-success'
                      : 'bg-muted text-muted-foreground'
                  }`}>
                    {product.is_active ? 'Active' : 'Inactive'}
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-sm font-medium text-[#2b3380]">{brand?.name || product.brand}</span>
                  <span className="text-muted-foreground">•</span>
                  <span className="text-muted-foreground">SKU: {product.sku_code}</span>
                </div>
                {product.live_date && (
                  <p className="text-sm text-muted-foreground">
                    Live since: {new Date(product.live_date).toLocaleString()}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center gap-2">
            {/* Edit Product Button */}
            <button
              onClick={() => setShowEditModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-secondary text-secondary-foreground rounded-lg hover:bg-secondary/90 transition-colors"
            >
              <Edit className="w-4 h-4" />
              Edit
            </button>

            {/* Refresh Price Button */}
            <button
              onClick={handleRefreshPrice}
              disabled={refreshing}
              className="flex items-center gap-2 px-4 py-2 bg-info text-info-foreground rounded-lg hover:bg-info/90 transition-colors disabled:opacity-50"
            >
              <RotateCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh Price
            </button>
          </div>
        </div>

        {/* Main Tabs */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 mb-6">
          <div className="flex border-b border-gray-100">
            <button
              onClick={() => setActiveMainTab('info')}
              className={`flex-1 py-4 px-6 text-sm font-medium transition-all flex items-center justify-center gap-2 ${
                activeMainTab === 'info'
                  ? 'border-b-2 border-[#2b3380] text-[#2b3380] bg-[#2b3380]/5'
                  : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
              }`}
            >
              <Info className="w-4 h-4" />
              Product Info
            </button>
            <button
              onClick={() => setActiveMainTab('selling')}
              className={`flex-1 py-4 px-6 text-sm font-medium transition-all flex items-center justify-center gap-2 ${
                activeMainTab === 'selling'
                  ? 'border-b-2 border-[#2b3380] text-[#2b3380] bg-[#2b3380]/5'
                  : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
              }`}
            >
              <DollarSign className="w-4 h-4" />
              MRP
            </button>
            <button
              onClick={() => setActiveMainTab('zsp')}
              className={`flex-1 py-4 px-6 text-sm font-medium transition-all flex items-center justify-center gap-2 ${
                activeMainTab === 'zsp'
                  ? 'border-b-2 border-[#2b3380] text-[#2b3380] bg-[#2b3380]/5'
                  : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
              }`}
            >
              <Tag className="w-4 h-4" />
              ZSP
            </button>
            <button
              onClick={() => setActiveMainTab('buyback')}
              className={`flex-1 py-4 px-6 text-sm font-medium transition-all flex items-center justify-center gap-2 ${
                activeMainTab === 'buyback'
                  ? 'border-b-2 border-[#2b3380] text-[#2b3380] bg-[#2b3380]/5'
                  : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
              }`}
            >
              <TrendingUp className="w-4 h-4" />
              MBP
            </button>
          </div>
        </div>

        {/* Tab Content */}
        {activeMainTab === 'info' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Product Info */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              {/* Image Gallery */}
              {product.images && product.images.length > 0 ? (
                <div className="mb-6">
                  {/* Main Image */}
                  <div className="mb-3 bg-gray-50 rounded-lg p-4 flex items-center justify-center" style={{ minHeight: '300px' }}>
                    <img
                      src={product.images[selectedImage]}
                      alt={`${product.model_name} - View ${selectedImage + 1}`}
                      className="max-w-full max-h-80 object-contain"
                    />
                  </div>
                  
                  {/* Thumbnail Gallery */}
                  {product.images.length > 1 && (
                    <div className="grid grid-cols-4 gap-2">
                      {product.images.map((image, idx) => (
                        <button
                          key={idx}
                          onClick={() => setSelectedImage(idx)}
                          className={`border-2 rounded-lg p-2 transition-all ${
                            selectedImage === idx
                              ? 'border-[#2b3380] bg-[#2b338015]'
                              : 'border-gray-200 hover:border-gray-400'
                          }`}
                        >
                          <img
                            src={image}
                            alt={`Thumbnail ${idx + 1}`}
                            className="w-full h-16 object-contain"
                          />
                        </button>
                      ))}
                    </div>
                  )}
                  
                  {/* Image Counter */}
                  <div className="text-center mt-2 text-sm text-gray-500">
                    {selectedImage + 1} / {product.images.length}
                  </div>
                </div>
              ) : (
                <div className="mb-6 bg-gray-100 h-80 rounded-lg flex items-center justify-center">
                  <div className="text-center">
                    <Package className="w-16 h-16 text-gray-400 mx-auto mb-2" />
                    <p className="text-gray-500 text-sm">No images available</p>
                  </div>
                </div>
              )}

              {/* Brand & Category */}
              <div className="mb-4">
                <h3 className="text-sm font-medium text-gray-500 mb-2">Brand</h3>
                <div className="flex items-center gap-3">
                  {brand?.logo_url ? (
                    <img 
                      src={brand.logo_url} 
                      alt={brand.name}
                      className="w-10 h-10 object-contain"
                    />
                  ) : (
                    <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
                      <Package className="w-5 h-5 text-gray-400" />
                    </div>
                  )}
                  <p className="text-lg font-semibold">{brand?.name || 'N/A'}</p>
                </div>
              </div>

              <div className="mb-4">
                <h3 className="text-sm font-medium text-gray-500 mb-2">Category</h3>
                <p className="text-lg">{product.category}</p>
              </div>

              {/* HSN Code */}
              {product.hsn_code && (
                <div className="mb-4">
                  <h3 className="text-sm font-medium text-gray-500 mb-2">HSN Code</h3>
                  <p className="text-lg font-mono">{product.hsn_code}</p>
                </div>
              )}
            </div>
          </div>

          {/* Specifications & Variants */}
          <div className="lg:col-span-2 space-y-6">
            {/* Features - Key Highlights from Smartprix */}
            {product.features && Object.keys(product.features).length > 0 ? (
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg shadow-md p-6">
                <h2 className="text-xl font-semibold mb-4 text-gray-900">Features</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {Object.entries(product.features).map(([key, value]) => {
                    if (!value) return null;
                    return (
                      <div key={key} className="bg-white rounded-lg p-3 shadow-sm flex items-start gap-3">
                        <div className="w-8 h-8 rounded-full bg-[#2b3380]/10 flex items-center justify-center flex-shrink-0">
                          {key === 'Connectivity' && <span className="text-[#2b3380] text-sm">📶</span>}
                          {key === 'Processor' && <span className="text-[#2b3380] text-sm">⚡</span>}
                          {key === 'Memory' && <span className="text-[#2b3380] text-sm">💾</span>}
                          {key === 'Battery' && <span className="text-[#2b3380] text-sm">🔋</span>}
                          {key === 'Display' && <span className="text-[#2b3380] text-sm">📱</span>}
                          {key === 'Camera' && <span className="text-[#2b3380] text-sm">📷</span>}
                          {key === 'OS' && <span className="text-[#2b3380] text-sm">🤖</span>}
                          {!['Connectivity', 'Processor', 'Memory', 'Battery', 'Display', 'Camera', 'OS'].includes(key) && <span className="text-[#2b3380] text-sm">•</span>}
                        </div>
                        <div className="flex-1">
                          <dt className="text-xs font-medium text-gray-500 uppercase mb-0.5">
                            {key.replace(/_/g, ' ')}
                          </dt>
                          <dd className="text-sm font-medium text-gray-800">{value}</dd>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : product.key_points && Object.keys(product.key_points).length > 0 ? (
              /* Fallback to Key Points for old products */
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg shadow-md p-6">
                <h2 className="text-xl font-semibold mb-4 text-gray-900">Features</h2>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {Object.entries(product.key_points).map(([key, value]) => {
                    if (!value) return null;
                    return (
                      <div key={key} className="bg-white rounded-lg p-3 shadow-sm">
                        <dt className="text-xs font-medium text-gray-500 uppercase mb-1">
                          {key.replace(/_/g, ' ')}
                        </dt>
                        <dd className="text-sm font-semibold text-gray-900">{value}</dd>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : null}

            {/* Detailed Specifications */}
            {product.specifications && Object.keys(product.specifications).length > 0 && (
              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-xl font-semibold">Detailed Specifications</h2>
                  <button
                    onClick={() => setShowAllSpecs(!showAllSpecs)}
                    className="text-sm text-[#2b3380] hover:text-[#1f2660] font-medium"
                  >
                    {showAllSpecs ? 'Show Less' : 'Show More'}
                  </button>
                </div>
                
                <div className="space-y-6">
                  {Object.entries(product.specifications).map(([sectionKey, sectionValue], index) => {
                    if (!sectionValue || (typeof sectionValue === 'object' && Object.keys(sectionValue).length === 0)) return null;
                    
                    // Key sections to always show (first 5)
                    const keySpecSections = ['Network', 'Launch', 'Body', 'Display', 'Platform', 'Memory'];
                    const isKeySection = keySpecSections.includes(sectionKey);
                    
                    // Hide non-key sections when collapsed
                    if (!showAllSpecs && !isKeySection && index >= 6) {
                      return null;
                    }
                    
                    // Handle nested objects (section with specs)
                    const renderSection = () => {
                      if (typeof sectionValue === 'object' && sectionValue !== null) {
                        return (
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            {Object.entries(sectionValue).map(([specKey, specValue]) => {
                              if (!specValue || specValue === '-') return null;
                              
                              return (
                                <div key={specKey} className="flex flex-col">
                                  <dt className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1">
                                    {specKey}
                                  </dt>
                                  <dd className="text-sm text-gray-900 whitespace-pre-wrap">
                                    {specValue}
                                  </dd>
                                </div>
                              );
                            })}
                          </div>
                        );
                      }
                      return <span className="text-sm text-gray-900 whitespace-pre-wrap">{sectionValue}</span>;
                    };
                    
                    return (
                      <div key={sectionKey} className="border-b border-gray-200 pb-4 last:border-b-0">
                        <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-center">
                          <span className="w-1 h-5 bg-[#2b3380] mr-2"></span>
                          {sectionKey}
                        </h3>
                        {renderSection()}
                      </div>
                    );
                  })}
                </div>
                
                {/* Show collapsed message */}
                {!showAllSpecs && Object.keys(product.specifications).length > 6 && (
                  <div className="mt-4 text-center">
                    <p className="text-sm text-gray-500 mb-2">
                      {Object.keys(product.specifications).length - 6} more sections available
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Variants (ZUPC) */}
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-semibold">Product Variants (ZUPC)</h2>
                <div className="flex items-center gap-3">
                  <span className="text-sm text-gray-500">
                    {variants.length} variant{variants.length !== 1 ? 's' : ''} available
                  </span>
                  <button
                    onClick={() => setShowAddZupcModal(true)}
                    className="flex items-center gap-2 px-3 py-1.5 bg-[#2b3380] text-white rounded-lg text-sm font-medium hover:bg-[#1e2560] transition-colors"
                    data-testid="add-zupc-btn"
                  >
                    <Plus className="w-4 h-4" />
                    Add ZUPC
                  </button>
                </div>
              </div>

              {variants.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <p>No variants created yet.</p>
                  <p className="text-sm mt-2">Click &quot;Add ZUPC&quot; to create variants manually, or variants are auto-created from scraping.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {variants.map(variant => (
                    <div
                      key={variant.id}
                      className="border rounded-lg p-4 hover:shadow-md transition-shadow"
                    >
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="mb-3">
                            <h3 className="font-semibold text-gray-900 text-lg">{variant.zupc_code}</h3>
                          </div>

                          <div className="grid grid-cols-2 gap-3 text-sm mb-3">
                            <div className="flex items-center gap-2">
                              <span className="text-gray-500">Storage:</span>
                              <span className="font-medium text-gray-900">{variant.memory || variant.storage}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="text-gray-500">MRP:</span>
                              <span className="font-medium text-gray-900">₹{variant.mrp?.toLocaleString() || 'N/A'}</span>
                            </div>
                          </div>
                          
                          {/* Available Colors as Attributes */}
                          {variant.available_colors && variant.available_colors.length > 0 && (
                            <div className="mt-3 pt-3 border-t border-gray-100">
                              <span className="text-xs text-gray-500 uppercase tracking-wider font-medium">Available Colors:</span>
                              <div className="flex flex-wrap gap-2 mt-2">
                                {variant.available_colors.map((color, idx) => (
                                  <span 
                                    key={idx} 
                                    className="text-xs px-2.5 py-1 bg-gradient-to-r from-gray-50 to-gray-100 border border-gray-200 rounded-full text-gray-700 font-medium"
                                  >
                                    {color}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}
                          
                          {/* Legacy single color (for backward compatibility) */}
                          {!variant.available_colors && variant.color && (
                            <div className="flex items-center gap-2 text-sm">
                              <span className="text-gray-500">Color:</span>
                              <span className="font-medium text-gray-900">{variant.color}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
        )}

        {/* MRP Tab */}
        {activeMainTab === 'selling' && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-xl font-bold text-gray-900">MRP by Variant</h2>
              </div>
            </div>
            
            {/* Base MRP from Store Prices */}
            {product.store_prices && product.store_prices.length > 0 && (
              <div className="mb-6 grid grid-cols-2 md:grid-cols-4 gap-4">
                {product.store_prices.map((sp, idx) => (
                  <div key={idx} className="bg-gradient-to-br from-green-50 to-emerald-50 p-4 rounded-xl border border-green-200">
                    <div className="text-sm text-gray-600 mb-1">{sp.store_name}</div>
                    <div className="text-2xl font-bold text-gray-900">₹{sp.price?.toLocaleString()}</div>
                    {sp.url && (
                      <a href={sp.url} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 hover:underline mt-1 inline-block">
                        View on store →
                      </a>
                    )}
                  </div>
                ))}
              </div>
            )}
            
            {/* Variant MRP Table - Using variants data directly */}
            <div className="overflow-x-auto rounded-xl border border-gray-100">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Variant</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Available Colors</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">MRP</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {variants.map((variant, idx) => {
                    const variantColors = variant.available_colors || product?.colors || [];
                    const mrpValue = variant.mrp || variant.flipkart_price || variant.amazon_price;
                    
                    return (
                      <tr key={variant.id || idx} className="hover:bg-gray-50 transition-colors">
                        <td className="px-4 py-4">
                          <span className="font-semibold text-gray-900">{variant.storage || variant.memory}</span>
                        </td>
                        <td className="px-4 py-4">
                          <div className="flex flex-wrap gap-1">
                            {variantColors.length > 0 ? (
                              <>
                                {variantColors.slice(0, 3).map((color, cIdx) => (
                                  <span key={cIdx} className="text-xs px-2 py-1 bg-purple-50 text-purple-700 border border-purple-200 rounded-full">{color}</span>
                                ))}
                                {variantColors.length > 3 && (
                                  <span className="text-xs px-2 py-1 bg-gray-100 text-gray-600 rounded-full">+{variantColors.length - 3}</span>
                                )}
                              </>
                            ) : (
                              <span className="text-xs text-gray-400">No colors</span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-4 text-right">
                          <input
                            type="number"
                            defaultValue={mrpValue || ''}
                            placeholder="Enter MRP"
                            className="w-28 px-2 py-1 text-right font-semibold text-gray-900 border border-gray-200 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            onBlur={async (e) => {
                              const newMrp = parseFloat(e.target.value);
                              if (newMrp && newMrp !== mrpValue) {
                                try {
                                  const response = await fetch(
                                    `${process.env.REACT_APP_BACKEND_URL}/api/scrape/variants/${variant.id}/update-mrp`,
                                    {
                                      method: 'PATCH',
                                      headers: {
                                        'Content-Type': 'application/json',
                                        'Authorization': `Bearer ${localStorage.getItem('token')}`
                                      },
                                      body: JSON.stringify({ mrp: newMrp })
                                    }
                                  );
                                  if (response.ok) {
                                    fetchProductDetails();
                                  }
                                } catch (err) {
                                  console.error('Error updating MRP:', err);
                                }
                              }
                            }}
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ZSP Tab - Zobox Selling Price by Grade */}
        {activeMainTab === 'zsp' && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-xl font-bold text-gray-900">ZSP (Zobox Selling Price) by Grade</h2>
                <p className="text-sm text-gray-500 mt-1">Grade-based pricing for each variant • All colors in same variant share same price</p>
              </div>
              <div className="flex items-center gap-2 px-3 py-1.5 bg-amber-50 border border-amber-200 rounded-lg">
                <Clock className="w-4 h-4 text-amber-600" />
                <span className="text-sm text-amber-700 font-medium">Pricing Module Pending</span>
              </div>
            </div>

            {/* Storage Variant Selector */}
            <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
              {variantStorages.map((storage, idx) => (
                <button
                  key={idx}
                  onClick={() => handleStorageChange(storage.storage_display || storage.storage)}
                  className={`px-4 py-2.5 rounded-xl whitespace-nowrap transition-all font-medium ${
                    selectedStorage === (storage.storage_display || storage.storage)
                      ? 'bg-[#2b3380] text-white shadow-md'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-200'
                  }`}
                >
                  {storage.storage_display || storage.storage}
                </button>
              ))}
            </div>

            {selectedStorage && (
              <div className="space-y-6">
                {/* Grade Pricing Table */}
                <div className="overflow-x-auto rounded-xl border border-gray-200">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Grade</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Description</th>
                        <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">ZSP</th>
                        <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {[
                        { grade: 'Excellent', color: 'emerald', description: 'Like new, minimal signs of use' },
                        { grade: 'Superb', color: 'green', description: 'Very good condition, light wear' },
                        { grade: 'Good', color: 'blue', description: 'Normal wear, fully functional' },
                        { grade: 'Fair', color: 'yellow', description: 'Visible wear, works perfectly' },
                        { grade: 'Okay', color: 'orange', description: 'Heavy wear, functional' },
                      ].map((item, idx) => (
                        <tr key={idx} className="hover:bg-gray-50 transition-colors">
                          <td className="px-4 py-4">
                            <div className="flex items-center gap-3">
                              <div className={`w-3 h-3 rounded-full bg-${item.color}-500`}></div>
                              <span className="font-semibold text-gray-900">{item.grade}</span>
                            </div>
                          </td>
                          <td className="px-4 py-4 text-sm text-gray-500">{item.description}</td>
                          <td className="px-4 py-4 text-right">
                            <span className="text-lg font-bold text-gray-300">-</span>
                          </td>
                          <td className="px-4 py-4 text-center">
                            <span className="px-2 py-1 text-xs font-medium bg-gray-100 text-gray-500 rounded-full">
                              Pending
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* AS-IS Section */}
                <div className="p-4 bg-gray-50 rounded-xl border border-gray-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-semibold text-gray-900">AS-IS Pricing</h3>
                      <p className="text-sm text-gray-500 mt-1">SKU-specific pricing for items sold without grading</p>
                    </div>
                    <div className="text-right">
                      <span className="text-lg font-bold text-gray-300">-</span>
                      <p className="text-xs text-gray-400 mt-1">Per SKU basis</p>
                    </div>
                  </div>
                </div>

                {/* Info Box */}
                <div className="p-4 bg-blue-50 rounded-xl border border-blue-100">
                  <div className="flex items-start gap-3">
                    <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-blue-800">How ZSP Works</p>
                      <ul className="text-sm text-blue-700 mt-2 space-y-1">
                        <li>• <strong>ZSP</strong> is calculated dynamically based on MBP + grade-specific margins</li>
                        <li>• All colors within the same variant (e.g., 128GB) share the same ZSP</li>
                        <li>• <strong>AS-IS</strong> pricing is SKU-specific and calculated separately</li>
                        <li>• Pricing algorithm will be configured in the Pricing Module</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* MBP Tab - Enhanced Table View */}
        {activeMainTab === 'buyback' && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            {/* Header */}
            <div className="mb-6">
              <h2 className="text-xl font-bold text-gray-900">MBP (Maximum Buyback Price)</h2>
              <p className="text-sm text-gray-500 mt-1">Enter competitor prices for all variants • Prices auto-save on click</p>
            </div>

            {/* All Variants Table */}
            {variantStorages.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="text-left p-3 font-semibold text-gray-700 border-b-2 border-gray-200 min-w-[120px]">Variant</th>
                      <th className="text-center p-3 font-semibold text-orange-600 border-b-2 border-gray-200 min-w-[200px]">
                        <div className="flex items-center justify-center gap-2">
                          <span className="w-6 h-6 rounded bg-orange-100 flex items-center justify-center text-xs font-bold">CF</span>
                          Cashify
                        </div>
                      </th>
                      <th className="text-center p-3 font-semibold text-blue-600 border-b-2 border-gray-200 min-w-[200px]">
                        <div className="flex items-center justify-center gap-2">
                          <span className="w-6 h-6 rounded bg-blue-100 flex items-center justify-center text-xs font-bold">BD</span>
                          Budli
                        </div>
                      </th>
                      <th className="text-center p-3 font-semibold text-green-600 border-b-2 border-gray-200 min-w-[200px]">
                        <div className="flex items-center justify-center gap-2">
                          <span className="w-6 h-6 rounded bg-green-100 flex items-center justify-center text-xs font-bold">RD</span>
                          RecycleDevice
                        </div>
                      </th>
                      <th className="text-center p-3 font-semibold text-purple-600 border-b-2 border-gray-200 min-w-[120px]">Our MBP</th>
                    </tr>
                  </thead>
                  <tbody>
                    {variantStorages.map((variant, idx) => {
                      const storageKey = variant.storage || variant.storage_display;
                      const displayKey = variant.storage_display || variant.storage;
                      
                      // Calculate competition avg and our MBP for this variant
                      const prices = [variant.cashify_mbp, variant.budli_mbp, variant.recycledevice_mbp].filter(p => p && p > 0);
                      const competitionAvg = prices.length > 0 ? Math.round(prices.reduce((a, b) => a + b, 0) / prices.length) : 0;
                      const calculatedOurMBP = competitionAvg > 0 ? Math.round(competitionAvg * (1 - marginPercent / 100)) : 0;
                      
                      return (
                        <tr key={idx} className={`border-b border-gray-100 hover:bg-gray-50 ${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}`}>
                          {/* Variant Name */}
                          <td className="p-3">
                            <div className="font-medium text-gray-900">{displayKey}</div>
                            {variant.our_mbp && (
                              <div className="text-xs text-green-600 mt-1">
                                ✓ Saved: ₹{variant.our_mbp.toLocaleString()}
                              </div>
                            )}
                          </td>
                          
                          {/* Cashify */}
                          <td className="p-2">
                            <div className="space-y-1">
                              <div className="flex items-center gap-1">
                                <span className="text-xs text-gray-500 w-8">₹</span>
                                <input
                                  type="number"
                                  placeholder="Price"
                                  className="w-20 px-2 py-1 text-sm border border-gray-200 rounded focus:ring-1 focus:ring-orange-300 focus:border-orange-400"
                                  value={getEditValue(storageKey, 'cashify_mbp') || (variant.cashify_mbp || '')}
                                  onChange={(e) => setEditValue(storageKey, 'cashify_mbp', e.target.value)}
                                  data-testid={`cashify-price-${idx}`}
                                />
                                <button
                                  onClick={() => handleSaveVariantMBP(storageKey, 'cashify', getEditValue(storageKey, 'cashify_mbp') || variant.cashify_mbp, getEditValue(storageKey, 'cashify_url') || variant.cashify_mbp_url)}
                                  disabled={!getEditValue(storageKey, 'cashify_mbp') && !variant.cashify_mbp}
                                  className="px-2 py-1 text-xs bg-orange-500 text-white rounded hover:bg-orange-600 disabled:bg-gray-200 disabled:text-gray-400"
                                  title="Save Cashify"
                                >
                                  Save
                                </button>
                              </div>
                              <div className="flex items-center gap-1">
                                <input
                                  type="url"
                                  placeholder="URL"
                                  className="flex-1 px-2 py-1 text-xs border border-gray-200 rounded focus:ring-1 focus:ring-orange-300"
                                  value={getEditValue(storageKey, 'cashify_url') || (variant.cashify_mbp_url || '')}
                                  onChange={(e) => setEditValue(storageKey, 'cashify_url', e.target.value)}
                                />
                                {(getEditValue(storageKey, 'cashify_url') || variant.cashify_mbp_url) && (
                                  <a
                                    href={getEditValue(storageKey, 'cashify_url') || variant.cashify_mbp_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="p-1 text-orange-500 hover:text-orange-700 hover:bg-orange-50 rounded"
                                    title="Open URL"
                                  >
                                    <ExternalLink className="w-3 h-3" />
                                  </a>
                                )}
                              </div>
                              {variant.cashify_last_scraped && (
                                <div className="text-xs text-gray-400">Updated: {formatLastScraped(variant.cashify_last_scraped)}</div>
                              )}
                            </div>
                          </td>
                          
                          {/* Budli */}
                          <td className="p-2">
                            <div className="space-y-1">
                              <div className="flex items-center gap-1">
                                <span className="text-xs text-gray-500 w-8">₹</span>
                                <input
                                  type="number"
                                  placeholder="Price"
                                  className="w-20 px-2 py-1 text-sm border border-gray-200 rounded focus:ring-1 focus:ring-blue-300 focus:border-blue-400"
                                  value={getEditValue(storageKey, 'budli_mbp') || (variant.budli_mbp || '')}
                                  onChange={(e) => setEditValue(storageKey, 'budli_mbp', e.target.value)}
                                  data-testid={`budli-price-${idx}`}
                                />
                                <button
                                  onClick={() => handleSaveVariantMBP(storageKey, 'budli', getEditValue(storageKey, 'budli_mbp') || variant.budli_mbp, getEditValue(storageKey, 'budli_url') || variant.budli_mbp_url)}
                                  disabled={!getEditValue(storageKey, 'budli_mbp') && !variant.budli_mbp}
                                  className="px-2 py-1 text-xs bg-blue-500 text-white rounded hover:bg-blue-600 disabled:bg-gray-200 disabled:text-gray-400"
                                  title="Save Budli"
                                >
                                  Save
                                </button>
                              </div>
                              <div className="flex items-center gap-1">
                                <input
                                  type="url"
                                  placeholder="URL"
                                  className="flex-1 px-2 py-1 text-xs border border-gray-200 rounded focus:ring-1 focus:ring-blue-300"
                                  value={getEditValue(storageKey, 'budli_url') || (variant.budli_mbp_url || '')}
                                  onChange={(e) => setEditValue(storageKey, 'budli_url', e.target.value)}
                                />
                                {(getEditValue(storageKey, 'budli_url') || variant.budli_mbp_url) && (
                                  <a
                                    href={getEditValue(storageKey, 'budli_url') || variant.budli_mbp_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="p-1 text-blue-500 hover:text-blue-700 hover:bg-blue-50 rounded"
                                    title="Open URL"
                                  >
                                    <ExternalLink className="w-3 h-3" />
                                  </a>
                                )}
                              </div>
                              {variant.budli_last_scraped && (
                                <div className="text-xs text-gray-400">Updated: {formatLastScraped(variant.budli_last_scraped)}</div>
                              )}
                            </div>
                          </td>
                          
                          {/* RecycleDevice */}
                          <td className="p-2">
                            <div className="space-y-1">
                              <div className="flex items-center gap-1">
                                <span className="text-xs text-gray-500 w-8">₹</span>
                                <input
                                  type="number"
                                  placeholder="Price"
                                  className="w-20 px-2 py-1 text-sm border border-gray-200 rounded focus:ring-1 focus:ring-green-300 focus:border-green-400"
                                  value={getEditValue(storageKey, 'recycledevice_mbp') || (variant.recycledevice_mbp || '')}
                                  onChange={(e) => setEditValue(storageKey, 'recycledevice_mbp', e.target.value)}
                                  data-testid={`recycledevice-price-${idx}`}
                                />
                                <button
                                  onClick={() => handleSaveVariantMBP(storageKey, 'recycledevice', getEditValue(storageKey, 'recycledevice_mbp') || variant.recycledevice_mbp, getEditValue(storageKey, 'recycledevice_url') || variant.recycledevice_mbp_url)}
                                  disabled={!getEditValue(storageKey, 'recycledevice_mbp') && !variant.recycledevice_mbp}
                                  className="px-2 py-1 text-xs bg-green-500 text-white rounded hover:bg-green-600 disabled:bg-gray-200 disabled:text-gray-400"
                                  title="Save RecycleDevice"
                                >
                                  Save
                                </button>
                              </div>
                              <div className="flex items-center gap-1">
                                <input
                                  type="url"
                                  placeholder="URL"
                                  className="flex-1 px-2 py-1 text-xs border border-gray-200 rounded focus:ring-1 focus:ring-green-300"
                                  value={getEditValue(storageKey, 'recycledevice_url') || (variant.recycledevice_mbp_url || '')}
                                  onChange={(e) => setEditValue(storageKey, 'recycledevice_url', e.target.value)}
                                />
                                {(getEditValue(storageKey, 'recycledevice_url') || variant.recycledevice_mbp_url) && (
                                  <a
                                    href={getEditValue(storageKey, 'recycledevice_url') || variant.recycledevice_mbp_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="p-1 text-green-500 hover:text-green-700 hover:bg-green-50 rounded"
                                    title="Open URL"
                                  >
                                    <ExternalLink className="w-3 h-3" />
                                  </a>
                                )}
                              </div>
                              {variant.recycledevice_last_scraped && (
                                <div className="text-xs text-gray-400">Updated: {formatLastScraped(variant.recycledevice_last_scraped)}</div>
                              )}
                            </div>
                          </td>
                          
                          {/* Our MBP - Editable */}
                          <td className="p-2 text-center">
                            <div className="space-y-1">
                              {/* Calculated suggestion */}
                              {competitionAvg > 0 && (
                                <div className="text-xs text-gray-500 mb-1">
                                  Suggested: ₹{calculatedOurMBP.toLocaleString()}
                                </div>
                              )}
                              {/* Editable Our MBP input */}
                              <div className="flex items-center justify-center gap-1">
                                <span className="text-xs text-gray-500">₹</span>
                                <input
                                  type="number"
                                  placeholder="Our MBP"
                                  className="w-20 px-2 py-1 text-sm border border-purple-200 rounded focus:ring-1 focus:ring-purple-300 focus:border-purple-400 text-center font-semibold"
                                  value={getEditValue(storageKey, 'our_mbp') || (variant.our_mbp || '')}
                                  onChange={(e) => setEditValue(storageKey, 'our_mbp', e.target.value)}
                                  data-testid={`our-mbp-${idx}`}
                                />
                                <button
                                  onClick={async () => {
                                    const ourMbpValue = getEditValue(storageKey, 'our_mbp') || variant.our_mbp || calculatedOurMBP;
                                    if (!ourMbpValue) return;
                                    try {
                                      const response = await fetch(
                                        `${process.env.REACT_APP_BACKEND_URL}/api/mbp-platforms/variant/${id}/save-our-mbp`,
                                        {
                                          method: 'POST',
                                          headers: {
                                            'Content-Type': 'application/json',
                                            'Authorization': `Bearer ${localStorage.getItem('token')}`
                                          },
                                          body: JSON.stringify({
                                            storage: storageKey,
                                            our_mbp: parseFloat(ourMbpValue),
                                            margin_percent: marginPercent
                                          })
                                        }
                                      );
                                      if (response.ok) {
                                        setEditValue(storageKey, 'our_mbp', '');
                                        await fetchVariantStorages();
                                      }
                                    } catch (error) {
                                      console.error('Error saving Our MBP:', error);
                                    }
                                  }}
                                  disabled={!getEditValue(storageKey, 'our_mbp') && !variant.our_mbp && !calculatedOurMBP}
                                  className="px-2 py-1 text-xs bg-purple-500 text-white rounded hover:bg-purple-600 disabled:bg-gray-200 disabled:text-gray-400"
                                  title="Save Our MBP"
                                >
                                  Save
                                </button>
                              </div>
                              {/* Competition avg display */}
                              {competitionAvg > 0 && (
                                <div className="text-xs text-gray-400">
                                  Avg: ₹{competitionAvg.toLocaleString()}
                                </div>
                              )}
                              {/* Use calculated button */}
                              {competitionAvg > 0 && (
                                <button
                                  onClick={() => setEditValue(storageKey, 'our_mbp', calculatedOurMBP.toString())}
                                  className="text-xs text-purple-600 hover:text-purple-800 underline"
                                >
                                  Use calculated
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                No variants found for this product
              </div>
            )}

            {/* Margin Control */}
            <div className="mt-6 p-4 bg-purple-50 rounded-xl border border-purple-100">
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-semibold text-purple-900">Margin Setting</h4>
                  <p className="text-sm text-purple-700">Our MBP = Competition Avg × (1 - Margin%)</p>
                </div>
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => setMarginPercent(Math.max(0, marginPercent - 1))}
                    className="w-8 h-8 rounded-lg bg-purple-200 text-purple-700 hover:bg-purple-300 flex items-center justify-center"
                  >
                    <Minus className="w-4 h-4" />
                  </button>
                  <div className="w-20 text-center">
                    <span className="text-2xl font-bold text-purple-700">{marginPercent}%</span>
                  </div>
                  <button
                    onClick={() => setMarginPercent(Math.min(50, marginPercent + 1))}
                    className="w-8 h-8 rounded-lg bg-purple-200 text-purple-700 hover:bg-purple-300 flex items-center justify-center"
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>

            {/* Help Text */}
            <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-100">
              <p className="text-sm text-blue-800">
                <strong>💡 Tip:</strong> Enter prices and URLs for each platform, then click Save. &quot;Last updated&quot; shows when you last updated each price.
              </p>
            </div>
          </div>
        )}
      </div>
      
      {/* Add ZUPC Modal */}
      {showAddZupcModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowAddZupcModal(false)}>
          <div className="bg-white rounded-xl p-6 w-full max-w-md shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-bold text-gray-900">Add New ZUPC Variant</h3>
              <button onClick={() => setShowAddZupcModal(false)} className="text-gray-400 hover:text-gray-600">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <p className="text-sm text-gray-500 mb-4">
              Add a new Storage × Color variant for {product?.model_name}
            </p>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Storage *</label>
                <input
                  type="text"
                  value={newZupc.storage}
                  onChange={(e) => setNewZupc({ ...newZupc, storage: e.target.value })}
                  placeholder="e.g., 256GB, 512GB, 1TB"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  data-testid="zupc-storage-input"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Color *</label>
                <input
                  type="text"
                  value={newZupc.color}
                  onChange={(e) => setNewZupc({ ...newZupc, color: e.target.value })}
                  placeholder="e.g., Black, Blue, Titanium"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  data-testid="zupc-color-input"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Flipkart Price</label>
                  <input
                    type="number"
                    value={newZupc.flipkart_price}
                    onChange={(e) => setNewZupc({ ...newZupc, flipkart_price: e.target.value })}
                    placeholder="e.g., 99990"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    data-testid="zupc-flipkart-price-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Amazon Price</label>
                  <input
                    type="number"
                    value={newZupc.amazon_price}
                    onChange={(e) => setNewZupc({ ...newZupc, amazon_price: e.target.value })}
                    placeholder="e.g., 98990"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    data-testid="zupc-amazon-price-input"
                  />
                </div>
              </div>
              
              <div className="text-xs text-gray-500 bg-gray-50 p-3 rounded-lg">
                <strong>Note:</strong> If prices are not provided, they will be inherited from the product&apos;s default store prices.
              </div>
            </div>
            
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowAddZupcModal(false)}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleAddZupc}
                disabled={addingZupc || !newZupc.storage || !newZupc.color}
                className="flex-1 px-4 py-2 bg-[#2b3380] text-white rounded-lg hover:bg-[#1e2560] disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
                data-testid="create-zupc-btn"
              >
                {addingZupc ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    Creating...
                  </>
                ) : (
                  <>
                    <Plus className="w-4 h-4" />
                    Create ZUPC
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default ProductDetail;
